import React, { useState, useMemo, useEffect } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase-client";

export type Role = {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  active: boolean;
  meta_data: Record<string, any>;
  created_at: string;
};

export type RolesPickerProps = {
  selectedIds?: string[];
  onPick: (roleIds: string[]) => void;
  onCancel?: () => void;
};

export function RolesPicker({ selectedIds = [], onPick, onCancel }: RolesPickerProps) {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(selectedIds);

  // Fetch roles on mount
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from("roles")
          .select("id, tenant_id, name, description, active, meta_data, created_at")
          .eq("active", true)
          .order("name", { ascending: true });

        if (cancel) return;

        if (fetchError) {
          console.error("Failed to fetch roles:", fetchError);
          setError("Failed to load roles");
          return;
        }

        setRoles(data ?? []);
      } catch (err) {
        if (!cancel) {
          console.error("Unexpected error fetching roles:", err);
          setError("An unexpected error occurred");
        }
      } finally {
        if (!cancel) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  const filteredRoles = useMemo(() => {
    if (!search) return roles;
    const searchLower = search.toLowerCase();
    return roles.filter(
      (role) => role.name.toLowerCase().includes(searchLower) || role.description?.toLowerCase().includes(searchLower),
    );
  }, [roles, search]);

  const handleRoleToggle = (roleId: string) => {
    setSelectedRoleIds((prev) => (prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]));
  };

  const handleConfirm = () => {
    onPick(selectedRoleIds);
  };

  return (
    <View testID="roles-picker" style={styles.container}>
      <ThemedText type="subtitle">Select Roles</ThemedText>

      <TextInput
        testID="roles-picker-search"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.background,
          },
        ]}
        placeholder="Search roles..."
        placeholderTextColor={colors.textSecondary}
        value={search}
        onChangeText={setSearch}
        autoFocus
        editable={!loading}
      />

      <ScrollView style={styles.scrollView}>
        {loading ? (
          <ThemedText style={styles.message}>Loading roles...</ThemedText>
        ) : error ? (
          <ThemedText style={[styles.message, { color: colors.red.text }]}>{error}</ThemedText>
        ) : filteredRoles.length > 0 ? (
          filteredRoles.map((role) => {
            const isSelected = selectedRoleIds.includes(role.id);
            return (
              <Pressable
                key={role.id}
                testID={`role-option-${role.id}`}
                style={[
                  styles.roleItem,
                  { borderBottomColor: colors.border },
                  isSelected && { backgroundColor: colors.backgroundHover },
                ]}
                onPress={() => handleRoleToggle(role.id)}
              >
                <View style={styles.roleContent}>
                  <MaterialCommunityIcons
                    name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                    size={24}
                    color={isSelected ? colors.primary : colors.text}
                  />
                  <View style={styles.roleInfo}>
                    <ThemedText type="defaultSemiBold">{role.name}</ThemedText>
                    {role.description && (
                      <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
                        {role.description}
                      </ThemedText>
                    )}
                  </View>
                </View>
              </Pressable>
            );
          })
        ) : (
          <ThemedText style={styles.message}>{search ? "No roles found" : "No roles available"}</ThemedText>
        )}
      </ScrollView>

      <View style={styles.footer}>
        <ThemedText style={styles.selectedCount}>{selectedRoleIds.length} selected</ThemedText>
        <View style={styles.footerButtons}>
          {onCancel && (
            <Button
              testID="roles-picker-cancel"
              text="Cancel"
              style={[styles.cancelButton, { borderColor: colors.border }]}
              textStyle={{ color: colors.text }}
              onPress={onCancel}
            />
          )}
          <Button
            testID="roles-picker-confirm"
            text="Confirm"
            style={styles.confirmButton}
            onPress={handleConfirm}
            enabled={selectedRoleIds.length > 0}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  scrollView: {
    maxHeight: 350,
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 20,
  },
  roleItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
  },
  roleContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  roleInfo: {
    flex: 1,
    gap: 2,
  },
  description: {
    fontSize: 13,
  },
  footer: {
    marginTop: 8,
  },
  selectedCount: {
    opacity: 0.7,
    marginBottom: 8,
  },
  footerButtons: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 32,
    paddingHorizontal: 16,
  },
});
